import React from 'react';
import { Box, TextField, FormControlLabel, Switch, Typography, Divider, Button, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { WaField, optionalLabel } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { DatePickerCore } from '../../../../flow-core/components/DatePickerCore';

export const DatePickerComponent = new FlowComponent({
  ...DatePickerCore,
  renderPreview(properties) {
    return (
      <WaField
        label={optionalLabel(properties.label || 'Select date', properties.required)}
        helper={properties['helper-text']}
        properties={properties}
      />
    );
  },
  renderPropertyEditor(properties, onChange) {
    var handleAddUnavailableDate = () => {
      var currentDates = properties['unavailable-dates'] || [];
      var today = new Date().toISOString().split('T')[0];
      onChange('unavailable-dates', [...currentDates, today]);
    };

    var handleRemoveUnavailableDate = (index) => {
      var currentDates = properties['unavailable-dates'] || [];
      var newDates = currentDates.filter((_, i) => i !== index);
      onChange('unavailable-dates', newDates);
    };

    var handleUnavailableDateChange = (index, value) => {
      var currentDates = properties['unavailable-dates'] || [];
      var newDates = [...currentDates];
      newDates[index] = value;
      onChange('unavailable-dates', newDates);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          inputProps={{ maxLength: 40 }}
          helperText={`${(properties.label || '').length}/40 characters`}
        />

        <TextField
          fullWidth
          type="date"
          label="Initial Value"
          value={properties['init-value'] || ''}
          onChange={(e) => onChange('init-value', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          helperText="Pre-selected date when the form loads"
        />

        <TextField
          fullWidth
          label="Helper Text"
          value={properties['helper-text'] || ''}
          onChange={(e) => onChange('helper-text', e.target.value)}
          size="small"
          helperText="Optional text shown below the date picker"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Date Range
        </Typography>

        <TextField
          fullWidth
          type="date"
          label="Minimum Date"
          value={properties['min-date'] || ''}
          onChange={(e) => onChange('min-date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          helperText="Earliest date users can select"
        />

        <TextField
          fullWidth
          type="date"
          label="Maximum Date"
          value={properties['max-date'] || ''}
          onChange={(e) => onChange('max-date', e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          helperText="Latest date users can select"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Unavailable Dates
        </Typography>

        <Box>
          {(properties['unavailable-dates'] || []).map((date, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
            >
              <TextField
                type="date"
                size="small"
                value={date || ''}
                onChange={(e) => handleUnavailableDateChange(index, e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveUnavailableDate(index)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddUnavailableDate}
            variant="outlined"
          >
            Add Unavailable Date
          </Button>
        </Box>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Validation
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />

        <TextField
          fullWidth
          label="Error Message"
          value={properties['error-message'] || ''}
          onChange={(e) => onChange('error-message', e.target.value)}
          size="small"
          helperText="Custom error message when validation fails"
        />

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          State
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={properties.enabled !== false}
              onChange={(e) => onChange('enabled', e.target.checked)}
            />
          }
          label="Enabled"
        />

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
